import {t} from "@lingui/core/macro";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {
    exchangeGoogleCodeForIDToken,
    getErrorMessageByCode,
    GoogleButton,
    GoogleIcon,
    initCodeClient,
    type CodeResponse,
} from "@khan/auth-google";
import {authType, type LoginType} from "@khan/auth-shared";
import {renderInfoToast} from "@khan/component-toast";
// eslint-disable-next-line no-restricted-imports -- TODO(FEI-7051): switch to useGql
import type {ApolloGqlClient} from "@khan/data-gql-apollo";
import {doNotTranslate} from "@khan/i18n";
import {type Location} from "@khan/urls";

import RequireReauth from "../require-reauth.tsx";

import ResponsiveLinkedAccountRow from "./responsive-linked-account-row.tsx";

interface Props {
    appleId: string | null | undefined;
    facebookId: string | null | undefined;
    microsoftId: string | null | undefined;
    googlePrimaryEmail: string | null | undefined;
    gqlClient: ApolloGqlClient;
    identifier: string | null | undefined;
    isClasslinkUser: boolean;
    linkAccountWith: (loginType: LoginType, token: string) => void;
    location: Location;
    renderThirdPartyDisconnectButton: (provider: LoginType) => React.ReactNode;
    setWarningMessage: (message: string | null) => void;
}

export const LinkGoogleAccount = ({
    gqlClient,
    googlePrimaryEmail,
    identifier,
    isClasslinkUser,
    setWarningMessage,
    facebookId,
    appleId,
    microsoftId,
    location,
    linkAccountWith,
    renderThirdPartyDisconnectButton,
}: Props): React.ReactNode => {
    const [showGoogleLoginButton, setShowGoogleLoginButton] =
        React.useState(false);

    /**
     * Notifies to Sentry in case we get any error from the Google auth API or
     * from the GraphQL mutation.
     * @param {Error} error The error thrown in the current context.
     */
    const handleGoogleLoginError = (error: Error) => {
        const errorMessage = getErrorMessageByCode(error.message);
        renderInfoToast(errorMessage);
        setShowGoogleLoginButton(true);
    };

    /**
     * Handle the response from Google's OAuth2 code flow.
     *
     * This is called when the user has been authenticated by Google and we have
     * a code to exchange for an ID token.
     */
    const handleCodeResponse = (response: CodeResponse) => {
        const code = response.code;
        exchangeGoogleCodeForIDToken(gqlClient, code, location.origin).then(
            (idToken) => {
                linkAccountWith("google", idToken);
            },
        );
    };

    /**
     * Try to add a Google account to the current user.
     *
     * This uses the Google Identity Services flow (GSI) to add a Google account
     * to the current user.
     */
    const authenticateWithGoogleIdentityServices = () => {
        initCodeClient(handleCodeResponse, setWarningMessage)
            .then((client) => {
                client.requestCode();
            })
            .catch((error) => {
                // Auth error
                if (error.error) {
                    handleGoogleLoginError(new Error(error.error));
                }
            });
    };

    const renderGoogleLoginButton = () => {
        if (showGoogleLoginButton) {
            return (
                <GoogleButton
                    reauth={true}
                    size="small"
                    continueUrl={location.pathname}
                    style={{minInlineSize: 210}}
                    context={authType.SIGNUP}
                />
            );
        }

        return (
            <RequireReauth
                identifier={identifier}
                showClasslinkAuthProvider={isClasslinkUser}
                /**
                 * Try to add Google account to the current user using the new login flow.
                 */
                onSuccess={authenticateWithGoogleIdentityServices}
                onFailure={() => {}}
                hasGoogleButton={false}
                // We check the other providers if the user has an account registered with them
                hasFacebookButton={!!facebookId}
                hasAppleButton={!!appleId}
                hasMicrosoftButton={!!microsoftId}
            >
                {(invokeReauth) => (
                    <Button
                        onClick={() => {
                            setWarningMessage(null);
                            invokeReauth();
                        }}
                        size="small"
                        kind="tertiary"
                    >
                        {t`Connect Google`}
                    </Button>
                )}
            </RequireReauth>
        );
    };

    return (
        <ResponsiveLinkedAccountRow
            icon={<GoogleIcon ariaHidden={true} />}
            label={doNotTranslate`Google`}
            content={googlePrimaryEmail || undefined}
            action={
                googlePrimaryEmail
                    ? renderThirdPartyDisconnectButton("google")
                    : renderGoogleLoginButton()
            }
        />
    );
};

export default LinkGoogleAccount;
